import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { simulateAnalysis } from '../services/staticAnalysis';
import Header from '../components/Header';
import '../App.css';

const Mahan02 = () => {
  const [selectedConcept, setSelectedConcept] = useState('guerre_de_course');
  const [modalContent, setModalContent] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState('✅ Conectado');

  // Conceptos estratégicos con análisis IA
  const conceptsData = {
    guerre_de_course: {
      title: "Guerre de Course (Guerra al Corso)",
      text: "Mahan critica severamente el ataque al comercio como estrategia principal. Argumenta que aunque destruye riqueza y causa sufrimiento, nunca gana guerras por sí solo si no se tiene el control del mar. El enemigo simplemente bloquea los puertos de los corsarios. Es el arma del débil, y la historia demuestra que siempre falla contra una marina organizada que mantiene el control de las líneas de comunicación."
    },
    fleet_in_being: {
      title: "Fleet in Being (Flota en Potencia)",
      text: "Concepto ejemplificado por el Almirante Torrington. Se refiere a mantener una flota naval activa y amenazante, pero evitando el combate decisivo hasta que las condiciones sean favorables. Mientras la flota exista ('in being'), el enemigo no puede ejecutar libremente invasiones o grandes movimientos de tropas por mar, ya que su propia retaguardia estaría amenazada."
    },
    weather_gage: {
      title: "Weather Gage (Posición de Barlovento)",
      text: "La ventaja táctica de estar 'viento arriba' del enemigo en la era de la vela. Permitía a la flota atacante elegir cuándo iniciar la batalla y maniobrar más libremente hacia el enemigo. La flota a sotavento a menudo estaba forzada a una postura defensiva. Mahan analiza cómo los ingleses preferían el barlovento para atacar, mientras los franceses a menudo preferían sotavento para retirarse fácilmente."
    },
    choke_points: {
      title: "Choke Points (Puntos de Estrangulamiento)",
      text: "Estrechos o canales geográficos obligatorios para el comercio y el movimiento naval (ej. Gibraltar, Canal de la Mancha, Sunda). Mahan argumenta que controlar estos puntos es infinitamente más valioso que controlar vastas extensiones de océano abierto, ya que fuerzan al enemigo a pasar por donde tú eres fuerte."
    },
    line_of_battle: {
      title: "Line of Battle (Línea de Batalla)",
      text: "La formación táctica estándar desde mediados del siglo XVII. Los barcos navegan en fila india para maximizar el fuego de sus costados (broadsides) sin bloquearse mutuamente. Mahan la defiende contra la 'melé' desorganizada, argumentando que la disciplina y la geometría del fuego son superiores al caos individual."
    },
    command_sea: {
      title: "Command of the Sea (Dominio del Mar)",
      text: "El objetivo supremo. No significa tener barcos en todas partes, sino tener una fuerza organizada capaz de neutralizar la flota enemiga y asegurar las propias líneas de comunicación. Quien tiene el dominio del mar puede proyectar poder en tierra, estrangular la economía enemiga y sostener sus propias fuerzas indefinidamente."
    }
  };

  // Datos de casos históricos
  const historicalCases = [
    {
      id: 'seven_years',
      title: 'Guerra de los Siete Años (1756-1763)',
      subtitle: 'El Triunfo del Poder Naval',
      description: 'Inglaterra adoptó una estrategia ofensiva naval implacable bajo Pitt. Destruyeron la flota francesa organizada en Lagos y Quiberon Bay.',
      result: 'Al aniquilar la capacidad naval francesa, sus colonias (Canadá, India) quedaron aisladas y cayeron inevitablemente.',
      control: 95,
      color: '#10b981'
    },
    {
      id: 'american_revolution',
      title: 'Independencia Americana (1775-1783)',
      subtitle: 'El Fracaso: Pérdida Temporal del Mar',
      description: '¿Por qué Inglaterra perdió las colonias? Mahan argumenta que perdió, temporalmente, el control del mar. Francia reconstruyó su armada.',
      result: 'La flota francesa bloqueó Chesapeake, impidiendo el rescate de Cornwallis. Sin mar, el ejército británico se rindió en Yorktown.',
      control: 35,
      color: '#ef4444'
    }
  ];

  // Bases navales estratégicas
  const navalBases = [
    { name: 'Gibraltar', strategic_value: 95, description: 'Control del Mediterráneo' },
    { name: 'Malta', strategic_value: 85, description: 'Centro del Mediterráneo' },
    { name: 'Suez', strategic_value: 90, description: 'Ruta a la India' },
    { name: 'Cabo de Buena Esperanza', strategic_value: 80, description: 'Ruta alternativa' },
    { name: 'Singapur', strategic_value: 88, description: 'Control del Estrecho de Malaca' }
  ];

  // Conclusiones clave
  const keyTakeaways = [
    {
      icon: '🌊',
      title: 'Vía, no Barrera',
      description: 'El mar es una carretera global. Quien la controla, controla la logística mundial.'
    },
    {
      icon: '🎯',
      title: 'Concentración',
      description: 'Nunca dividir la flota. El objetivo es la flota enemiga, no posiciones costeras aisladas.'
    },
    {
      icon: '⚔️',
      title: 'Comando vs. Corso',
      description: 'Atacar el comercio molesta, pero solo el Control del Mar gana guerras.'
    },
    {
      icon: '🛡️',
      title: 'Defensa Ofensiva',
      description: 'La mejor defensa costera es combatir a la flota enemiga en sus propios puertos.'
    }
  ];

  const handleAnalyzeConcept = async () => {
    const concept = conceptsData[selectedConcept];
    setIsLoading(true);
    setIsModalOpen(true);
    setModalContent('Generando análisis...');

    try {
      const response = await simulateAnalysis('mahan', concept.title);
      setModalContent(response);
    } catch (error) {
      console.error('Error analyzing concept:', error);
      setModalContent(`**Error de Análisis:** No se pudo cargar el análisis.\n\n*Por favor intenta nuevamente.*`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }}>
      <Header />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <Link to="/">
            <button style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}>
              ← Volver al Inicio
            </button>
          </Link>
        </div>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ 
            marginBottom: '0.5rem', 
            color: '#0EA5E9', 
            fontWeight: 'bold', 
            letterSpacing: '0.1em', 
            textTransform: 'uppercase', 
            fontSize: '0.875rem' 
          }}>
            {authStatus}
          </div>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            marginBottom: '0.5rem',
            background: 'linear-gradient(135deg, #0284C7 0%, #0EA5E9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            La Influencia del Poder Naval en la Historia
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            color: '#94a3b8',
            marginBottom: '1rem'
          }}>
            Un análisis visual de la obra seminal de Alfred Thayer Mahan (1660-1783). Descubra cómo el control del mar determina el destino de las naciones, la prosperidad económica y la hegemonía global.
          </p>
        </div>

        {/* Sección 1: Tesis Central */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ 
                color: '#0EA5E9', 
                fontSize: '2rem', 
                marginBottom: '1rem',
                borderLeft: '4px solid #F59E0B',
                paddingLeft: '1rem'
              }}>
                El Mar: Un Gran Bien Común
              </h2>
              <p style={{ color: '#cbd5e1', marginBottom: '1rem', lineHeight: '1.8' }}>
                Mahan no ve el mar como una barrera, sino como una vasta carretera abierta ("a great common"). La tesis central es inequívoca: el poder naval no es un fin en sí mismo, sino un medio para asegurar el comercio.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ color: '#F59E0B', marginRight: '0.5rem', fontSize: '1.2rem' }}>⚓</span>
                  <span style={{ color: '#f1f5f9' }}><strong>Comercio Marítimo:</strong> La fuente de la riqueza nacional.</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ color: '#F59E0B', marginRight: '0.5rem', fontSize: '1.2rem' }}>⚓</span>
                  <span style={{ color: '#f1f5f9' }}><strong>Marina Mercante:</strong> El vehículo de esa riqueza.</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#F59E0B', marginRight: '0.5rem', fontSize: '1.2rem' }}>⚓</span>
                  <span style={{ color: '#f1f5f9' }}><strong>Marina Militar:</strong> La protección necesaria para asegurar el flujo comercial.</span>
                </div>
              </div>
            </div>
            
            {/* Estructura del Poder Nacional - Gráfico Simulado */}
            <div style={{
              background: 'rgba(15, 23, 42, 0.8)',
              borderRadius: '1rem',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <h4 style={{ color: '#0EA5E9', marginBottom: '1rem' }}>Estructura del Poder Nacional</h4>
              <div style={{ position: 'relative', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'conic-gradient(#0EA5E9 0deg 180deg, #F59E0B 180deg 270deg, #10b981 270deg 360deg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem'
                  }}>
                    ⚓
                  </div>
                </div>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', fontStyle: 'italic' }}>
                La marina militar es solo una rama; el comercio es la raíz.
              </p>
            </div>
          </div>
        </div>

        {/* Sección 2: Los 6 Elementos */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          <h2 style={{ color: '#f1f5f9', fontSize: '2rem', textAlign: 'center', marginBottom: '1rem' }}>
            Los 6 Pilares del Poder Naval
          </h2>
          <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '2rem' }}>
            Mahan establece seis condiciones fundamentales que dictan si una nación puede aspirar a la hegemonía marítima.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {[
              { title: '1. Posición Geográfica', desc: 'Ventaja insular: sin necesidad de defensa terrestre masiva. Control de puntos estratégicos.' },
              { title: '2. Conformación Física', desc: 'Puertos naturales profundos. Suelos pobres empujan a la población al mar (ej. Holanda).' },
              { title: '3. Extensión Territorial', desc: 'Longitud de costa vs. capacidad defensiva. Demasiada costa sin población es debilidad.' },
              { title: '4. Población Marítima', desc: 'Reserva de gente de mar disponible para la guerra.' },
              { title: '5. Carácter Nacional', desc: 'Aptitud y deseo de comercio, no solo de oro.' },
              { title: '6. Gobierno', desc: 'Visión estratégica y consistencia en el mantenimiento de la flota.' }
            ].map((element, index) => (
              <div key={index} style={{
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                borderLeft: `4px solid ${index < 3 ? '#0EA5E9' : '#F59E0B'}`
              }}>
                <h4 style={{ color: '#f1f5f9', fontSize: '1rem', marginBottom: '0.5rem' }}>
                  {element.title}
                </h4>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  {element.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Sección 3: Explorador Interactivo con IA */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          <h2 style={{ 
            color: '#f1f5f9', 
            fontSize: '2rem', 
            textAlign: 'center', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            Enciclopedia Estratégica con IA
            <span style={{ 
              fontSize: '0.7rem', 
              background: '#F59E0B', 
              color: '#0f172a', 
              padding: '0.25rem 0.5rem', 
              borderRadius: '0.25rem',
              fontWeight: 'bold'
            }}>
              INTERACTIVO
            </span>
          </h2>
          <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '2rem' }}>
            Profundiza en los conceptos clave de Mahan. Selecciona una categoría para ver el análisis base y utiliza la funcionalidad de Gemini para expandir el conocimiento.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
            {/* Controles */}
            <div>
              <h4 style={{ color: '#f1f5f9', marginBottom: '1rem' }}>Selecciona un Concepto Estratégico:</h4>
              <select 
                value={selectedConcept}
                onChange={(e) => setSelectedConcept(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  background: 'rgba(15, 23, 42, 0.8)',
                  color: '#f1f5f9',
                  fontSize: '1rem'
                }}
              >
                {Object.entries(conceptsData).map(([key, concept]) => (
                  <option key={key} value={key}>{concept.title}</option>
                ))}
              </select>

              <div style={{
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginTop: '1rem'
              }}>
                <h5 style={{ color: '#0EA5E9', marginBottom: '0.5rem' }}>Herramientas de Análisis</h5>
                <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1rem' }}>
                  Utiliza la IA para generar nuevas perspectivas sobre cómo este concepto se aplicó en el libro.
                </p>
                <button
                  onClick={handleAnalyzeConcept}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: 'linear-gradient(135deg, #4F46E5 0%, #0EA5E9 100%)',
                    color: 'white',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span>🤖</span>
                  Analizar con Gemini
                </button>
              </div>
            </div>

            {/* Display */}
            <div style={{
              background: 'rgba(15, 23, 42, 0.8)',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              borderLeft: '4px solid #0EA5E9'
            }}>
              <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem', marginBottom: '1rem' }}>
                {conceptsData[selectedConcept].title}
              </h3>
              <p style={{ color: '#cbd5e1', lineHeight: '1.6' }}>
                {conceptsData[selectedConcept].text}
              </p>
            </div>
          </div>
        </div>

        {/* Sección 4: Casos Históricos */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          <h2 style={{ color: '#f1f5f9', fontSize: '2rem', textAlign: 'center', marginBottom: '2rem' }}>
            Lecciones de la Historia (1660-1783)
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
            {historicalCases.map((case_study, index) => (
              <div key={case_study.id} style={{
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                borderLeft: `4px solid ${case_study.color}`
              }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                  {case_study.title}
                </h3>
                <h4 style={{ color: case_study.color, fontSize: '1rem', marginBottom: '1rem' }}>
                  {case_study.subtitle}
                </h4>
                <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  {case_study.description}
                </p>
                
                {/* Control del Mar */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Control del Mar</span>
                    <span style={{ color: case_study.color, fontWeight: 'bold' }}>{case_study.control}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: 'rgba(148, 163, 184, 0.2)',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${case_study.control}%`,
                      height: '100%',
                      background: case_study.color,
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>
                
                <div style={{
                  background: `${case_study.color}20`,
                  borderRadius: '0.25rem',
                  padding: '0.75rem'
                }}>
                  <strong style={{ color: case_study.color, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>
                    Resultado Estratégico:
                  </strong>
                  <p style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>
                    {case_study.result}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sección 5: Bases Navales Estratégicas */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          <h2 style={{ color: '#f1f5f9', fontSize: '2rem', textAlign: 'center', marginBottom: '1rem' }}>
            Peso Estratégico de las Bases Navales
          </h2>
          <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '2rem' }}>
            Una flota sin bases es un vagabundo indefenso. Mahan destaca la importancia de posiciones geográficas clave.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {navalBases.map((base, index) => (
              <div key={index} style={{
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '0.5rem',
                padding: '1rem',
                textAlign: 'center',
                border: '1px solid rgba(148, 163, 184, 0.1)'
              }}>
                <h4 style={{ color: '#f1f5f9', marginBottom: '0.5rem' }}>{base.name}</h4>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: `conic-gradient(#0EA5E9 0deg ${base.strategic_value * 3.6}deg, rgba(148, 163, 184, 0.2) ${base.strategic_value * 3.6}deg 360deg)`,
                  margin: '0 auto 0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  ⚓
                </div>
                <div style={{ color: '#0EA5E9', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {base.strategic_value}%
                </div>
                <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  {base.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Sección 6: Conclusiones Clave */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          <h2 style={{ color: '#f1f5f9', fontSize: '2rem', textAlign: 'center', marginBottom: '2rem' }}>
            Conclusiones Clave del Marco de Mahan
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {keyTakeaways.map((takeaway, index) => (
              <div key={index} style={{
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                textAlign: 'center',
                borderTop: '4px solid #0EA5E9'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{takeaway.icon}</div>
                <h4 style={{ color: '#f1f5f9', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                  {takeaway.title}
                </h4>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  {takeaway.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          marginTop: '3rem', 
          padding: '2rem',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <Link to="/mahan01">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              ⬅️ Análisis Estratégico
            </button>
          </Link>
          
          <Link to="/">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'rgba(148, 163, 184, 0.2)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              🏠 Dashboard
            </button>
          </Link>
        </div>
      </div>

      {/* Modal para análisis IA */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              padding: '2rem',
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem' }}>
                ⚓ Análisis Naval con IA
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              {isLoading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚓</div>
                  <p>Generando análisis con IA...</p>
                  <div style={{ 
                    width: '100%', 
                    height: '4px', 
                    background: 'rgba(148, 163, 184, 0.2)', 
                    borderRadius: '2px',
                    marginTop: '1rem',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '30%',
                      height: '100%',
                      background: 'linear-gradient(90deg, #0EA5E9, #F59E0B)',
                      borderRadius: '2px',
                      animation: 'loading 2s ease-in-out infinite'
                    }} />
                  </div>
                </div>
              ) : (
                <div 
                  style={{ 
                    color: '#cbd5e1',
                    lineHeight: '1.8',
                    fontSize: '1.1rem'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: modalContent
                      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #f1f5f9; font-weight: bold;">$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em style="color: #0EA5E9;">$1</em>')
                      .replace(/\n\n/g, '</p><p style="margin: 1.5rem 0;">')
                      .replace(/^/, '<p style="margin: 1.5rem 0;">')
                      .replace(/$/, '</p>')
                      .replace(/(\d+\.\s)/g, '<br/><strong style="color: #0EA5E9;">$1</strong>')
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mahan02;